/* eslint-disable no-underscore-dangle,no-param-reassign,no-use-before-define,max-len */

import { Logger, i18n } from 'vue-infra';
import moment from 'moment';
import { isNull, merge, find, each, isUndefined, cloneDeep, filter, includes, has, get, set, isEmpty, map, keys, without, pick, pickBy } from 'lodash';
import { WIDGET_PATHS, CHANGE_AGENTS_FILTER_ACTION_NAME } from '@core/store';
import {
  UNASSIGNED_SKILL_ID,
  LOCAL_STORAGE_SETTINGS_PER_USER,
  PRIVILEGES,
  SESSION_SETTINGS,
  STOP_SUBSCRIPTION_INTERVAL_SECONDS,
  MAX_AMW_VERSION_NUMBER,
  WIDGET_DEFAULT_HEIGHT,
  WIDGET_NAMES_AFFECTED_BY_METRICS_AMOUNT,
  DATA_LOADING_STATES,
  SUBSCRIPTION_STATUSES,
  AC_RETRY_AFTER_ERROR_INTERVAL,
  SITE_SETTINGS_VERSION_KEY,
  WIDGET_LOGICAL_NAMES,
  FEATURES,
  AXIOS_RETRY_OBJECT,
  METRIC_NAMES,
  SOURCE_CONTEXT_NAMES,
  THROTTLING_STATUS_CODE,
  WIDGET_ENABLING_OTHER_WIDGETS_METRICS,
  MAX_WIDGET_WIDTH,
  PRESETS,
  SORT_DIRECTION,
  WIDGET_MODES,
  PRESET_VALIDATION_I18N_KEYS,
  LOCAL_STORAGE_ALERTS_PER_USER,
  COLLAPSED_WIDGET_HEIGHT,
  MAP_ASSOCIATED_WIDGETS_COLLAPSE,
  GRID_LAYOUT_GLOBAL_SETTINGS,
  MAP_WIDGET_INITIAL_HEIGHT,
} from '@config/Consts';
import { defaultSettings } from '@config/defaultSettings';
import defaultAccountSettings from '@config/defaultAccountSettings';
import { Subscription } from '@core/common/Subscription';
import acService from '@core/services/ac/ACService';
import { ACUserSettings } from '@core/services/ac/ACUserSettings';
import { RESOURCES } from '@core/clients/AcClient';
import {
  convertVersionToNumber,
  mergeSiteSettingConfiguration,
  overwriteSiteSettingParams,
  parseMetricsConfiguration,
  extractConfigurationFromSiteSetting,
  updateSiteSettingsPerSpecificFeature,
  getMetricSettingsKey,
  lowerCaseCompare,
} from '@core/utils/utils';
import eventBusEmitter from '@core/event-bus/EventBusEmitter';

const logger = Logger.getLogger('rootActions');

let stopSubscriptionRequested = false;
let stopSubscriptionTimeout;
let setInitDataTimeout;
const updateSubscriptionTimeout = {};
const lastSubscriptionTime = {};
let acSubscriptions;

/* ****************************** SETTINGS ************************* */

const _getSettingsFromLocalstorage = (getters) => {
  let settings;
  if (localStorage) {
    settings = localStorage.getItem(
      LOCAL_STORAGE_SETTINGS_PER_USER(getters.getLoggedInUserId));
    if (settings) {
      settings = JSON.parse(settings);
    }
  }
  return settings;
};

const _adjustAgentAlertSingleApi = (alerts) => {
  const legacyAgentStatusAlert = get(alerts, `agents.${METRIC_NAMES.AGENT_STATUS}`);
  const agentStatusAlert = get(alerts, `agents.${METRIC_NAMES.AGENT_VIEW_AGENT_STATUS}`);
  if (legacyAgentStatusAlert && !agentStatusAlert) {
    set(alerts, `agents.${METRIC_NAMES.AGENT_VIEW_AGENT_STATUS}`, legacyAgentStatusAlert);
  }

  const legacyAgentStatusDurationAlert = get(alerts, `agents.${METRIC_NAMES.AGENT_STATUS_DURATION}`);
  const agentStatusDurationAlert = get(alerts, `agents.${METRIC_NAMES.AGENT_VIEW_AGENT_STATUS_DURATION}`);
  if (legacyAgentStatusDurationAlert && !agentStatusDurationAlert) {
    set(alerts, `agents.${METRIC_NAMES.AGENT_VIEW_AGENT_STATUS_DURATION}`, legacyAgentStatusDurationAlert);
  }

  const agentStatusWithReasonDurationAlert = get(alerts, `agents.${METRIC_NAMES.AGENT_VIEW_AGENT_STATUS_WITH_REASON_DURATION}`);
  if (agentStatusDurationAlert && !agentStatusWithReasonDurationAlert) {
    set(alerts, `agents.${METRIC_NAMES.AGENT_VIEW_AGENT_STATUS_WITH_REASON_DURATION}`, agentStatusDurationAlert);
  }


  return alerts;
};

const _getAlertsFromLocalstorage = (getters) => {
  let alerts;
  if (localStorage) {
    alerts = localStorage.getItem(
      LOCAL_STORAGE_ALERTS_PER_USER(getters.getLoggedInUserId));
    if (alerts) {
      alerts = JSON.parse(alerts);
    }
  }
  alerts = _adjustAgentAlertSingleApi(alerts);
  return alerts;
};

const _getSettingsIndexes = (localVersion, currentVersion, settings) => {
  let localVersionIndex = null;
  let currentVersionIndex = null;
  each(settings, (item, index) => {
    const fromVersion = convertVersionToNumber(item.AMW_version_from);
    const toVersion = convertVersionToNumber(get(item, 'AMW_version_to')) || MAX_AMW_VERSION_NUMBER;
    if (isNull(localVersionIndex)) {
      if (localVersion >= fromVersion && localVersion <= toVersion) {
        localVersionIndex = index;
      }
    }
    if (isNull(currentVersionIndex)) {
      if (currentVersion >= fromVersion && currentVersion <= toVersion) {
        currentVersionIndex = index;
      }
    }
  });
  return { localVersionIndex, currentVersionIndex };
};

const _setLocalStorageUserSettings = async (getters, userSettings) => {
  if (localStorage) {
    localStorage.setItem(
      LOCAL_STORAGE_SETTINGS_PER_USER(getters.getLoggedInUserId), userSettings);
  }
};

const _setLocalStorageUserAlerts = async (getters, alerts) => {
  if (localStorage) {
    localStorage.setItem(
      LOCAL_STORAGE_ALERTS_PER_USER(getters.getLoggedInUserId), alerts);
  }
};

const _updateUserSettingsStoreAndLocalStorage = async ({ commit, getters }, userSettings) => {
  commit('setSettings', userSettings);
  await _setLocalStorageUserSettings(getters, JSON.stringify(userSettings));
};

// Beside the update action, the following method also returns rather the settings change
// caused a layout change (like height change)
const updateUserSettingsInStore = async ({ getters, commit },
  userSettings = getters.getUserSettings, defaultVersionSettings) => {
  let layoutChanged = false;
  // Checking if we haven't reached here from initAccountSettings flow before
  // initUserSettings flow was already completed.
  if (!isEmpty(userSettings)) {
    const settings = get(userSettings, 'widget_settings.settings');
    each(settings, (item) => {
      item.config.authorized = getters.getWidgetPermission(item.name);
      if (item.name === WIDGET_LOGICAL_NAMES.SHIFT_STATUS) {
        if (!getters.getWidgetPermission(WIDGET_LOGICAL_NAMES.IN_QUEUE)) {
          // If it's Shift status widget and there are no permissions for In-Queue widget,
          // Shift status should take max workspace width
          if (!item.config.isCollapsed) {
            item.config.h = WIDGET_DEFAULT_HEIGHT;
          }
          item.config.w = MAX_WIDGET_WIDTH;
          item.config.x = 0;
        } else if (defaultVersionSettings && item.config.w === MAX_WIDGET_WIDTH) {
          // In case of switch from Skills privileges to standard view for same user,
          // we need to reset Shift Status width and x position
          // defaultVersionSettings are passed only on initUserSettings
          const fallbackSettings = get(defaultVersionSettings, 'widget_settings.settings', []);
          const shiftStatusSettings = fallbackSettings.find(
            widget => widget.name === WIDGET_LOGICAL_NAMES.SHIFT_STATUS);
          const defaultWidth = get(shiftStatusSettings, 'config.w');
          const defaultX = get(shiftStatusSettings, 'config.x');
          item.config.w = defaultWidth;
          item.config.x = defaultX;
        }
      }
      if (includes(WIDGET_NAMES_AFFECTED_BY_METRICS_AMOUNT, item.name) &&
        !item.config.isCollapsed) {
        const previousHeight = item.config.h;
        item.config.h = WIDGET_DEFAULT_HEIGHT;
        layoutChanged = previousHeight !== item.config.h;
      }
    });
    _updateUserSettingsStoreAndLocalStorage({ commit, getters }, userSettings);
  }
  return layoutChanged;
};

const _overwriteUserSettings = (versionSettings, newSettings) => {
  const forceUpdateParams = get(versionSettings, 'force_update_params') || [];
  // we overwrite the params defined in force_update_params
  each(forceUpdateParams, (param) => {
    set(newSettings, param, get(versionSettings, param));
  });
  set(newSettings, 'AMW_version_from', get(versionSettings, 'AMW_version_from'));
  set(newSettings, 'force_update_params', get(versionSettings, 'force_update_params'));
  if (get(versionSettings, 'AMW_version_to')) {
    set(newSettings, 'AMW_version_to', get(versionSettings, 'AMW_version_to'));
  }
};

const _mergeAndOverwriteUserSettings =
  (versionSettings, localUserSettings, defaultVersionSettings) => {
    const mergedSettings = merge(versionSettings, localUserSettings);
    // After the merge that mutated versionSettings,
    // I need to refer to the original defaultSettings
    _overwriteUserSettings(defaultVersionSettings, mergedSettings);
    return mergedSettings;
  };

const _applyUserSettings = async (dispatch, getters, commit, localSettingsConfig) => {
  const currentAmwVersion = getters.getNumericAmwVersion;
  const localAmwVersion = localSettingsConfig.localUserAmwVersion;
  const indexes = _getSettingsIndexes(localAmwVersion, currentAmwVersion, defaultSettings);
  const versionsMatch = indexes.localVersionIndex === indexes.currentVersionIndex;
  const defaultVersionSettings = localSettingsConfig.defaultVersionSettings;
  const versionSettings = cloneDeep(defaultVersionSettings);
  const localUserSettings = localSettingsConfig.localUserSettings;
  let newSettings;
  if (versionsMatch) {
    const localFromVersion = get(localUserSettings, 'AMW_version_from');
    const currentFromVersion = get(versionSettings, 'AMW_version_from');
    // localFromVersion might be null if the local storage key does not exist or is an empty object
    if (localFromVersion && localFromVersion !== currentFromVersion) {
      // case of Rollback
      // Remove widgets from higher versions (rollback)
      const defaultSettingsLength =
        versionSettings.widget_settings.settings.length;
      const localUserSettingsLength = localUserSettings.widget_settings.settings.length;
      const unusedWidgets = localUserSettingsLength - defaultSettingsLength;
      if (unusedWidgets > 0) {
        localUserSettings.widget_settings.settings.splice(-unusedWidgets);
      }
      newSettings = _mergeAndOverwriteUserSettings(versionSettings, localUserSettings,
        defaultVersionSettings);
    } else {
      // we just apply a merge
      newSettings = merge(versionSettings, localUserSettings);
    }
  } else {
    // This is an upgrade case
    newSettings = _mergeAndOverwriteUserSettings(versionSettings, localUserSettings,
      defaultVersionSettings);
  }
  newSettings.widget_settings_amw_version = currentAmwVersion;
  await updateUserSettingsInStore({ getters, commit }, newSettings, defaultVersionSettings);
  _restorePresetValues({ dispatch, getters, commit }, newSettings);
};

const _setUserFilter = async ({ getters, commit }, filterName, filterValue) => {
  const userSettings = getters.getUserSettings;
  const userFilters = get(userSettings, 'widget_settings.global_filters');
  userFilters[filterName] = filterValue;
  await updateUserSettingsInStore({ getters, commit }, userSettings);
};

const _getAssociatedWidgetCollapse = (widgetSettingsArray, widgetName) => {
  const result = [widgetSettingsArray.find(item => item.name === widgetName)];
  const associatedWidgets = get(MAP_ASSOCIATED_WIDGETS_COLLAPSE, widgetName);
  if (associatedWidgets) {
    each(associatedWidgets, associatedWidget =>
      result.push(widgetSettingsArray.find(item => item.name === associatedWidget)));
  }
  return result;
};

const saveDisplayState = async ({ getters, commit }, widgetDetails) => {
  const userSettings = cloneDeep(getters.getUserSettings);
  const widgetSettingsArray = get(userSettings, 'widget_settings.settings');
  if (widgetSettingsArray) {
    const widgetSettingsProps =
      _getAssociatedWidgetCollapse(widgetSettingsArray, widgetDetails.name);
    each(widgetSettingsProps, (widgetSettings) => {
      let h;
      let expandedH;
      if (widgetSettings) {
        if (widgetDetails.isCollapsed) {
          expandedH = widgetSettings.config.h;
          h = COLLAPSED_WIDGET_HEIGHT;
          widgetSettings.config.expandedH = expandedH;
          widgetSettings.config.h = h;
        } else {
          // there are some very rare situations where the previous expandedH has been stored with the collapsed height.
          // in this case we store the fallback height
          h = widgetSettings.config.expandedH === COLLAPSED_WIDGET_HEIGHT ? MAP_WIDGET_INITIAL_HEIGHT[widgetSettings.config.i] : widgetSettings.config.expandedH;
          widgetSettings.config.h = h;
          expandedH = h;
        }
        widgetSettings.config.isCollapsed = widgetDetails.isCollapsed;
        // we do not want to update the new height of the widget as it repaints the widget and can make an issue for rendering heavy number of agent list.
        // we want to update only the expandedH to the store
        commit('updateWidgetExpandedH', { widgetName: widgetSettings.config.i, expandedH, h, isCollapsed: widgetDetails.isCollapsed });
        eventBusEmitter.emit('resizeGridItem', { id: widgetSettings.config.i, x: widgetSettings.config.x, y: widgetSettings.config.y, h, w: widgetSettings.config.w });
        // eventBusEmitter.emit('gridItemResized', { id: widgetSettings.config.i, x: widgetSettings.config.x, y: widgetSettings.config.y, h, w: widgetSettings.config.w });
      }
    });
    _setLocalStorageUserSettings(getters, JSON.stringify(userSettings));
  }
};
// We need to use the force_update_params of each version in the skipped range in order to update
// mergedSiteSettingConfiguration to the correct values as if the upgrade was done gradually
// from one version to another
const _adjustSkippedVersionsConfiguration =
  (indexes, mergedSiteSettingConfiguration) => {
    let cumulativeForceUpdateParams = [];
    const currentVersionIndex = indexes.currentVersionIndex;
    for (let i = indexes.localVersionIndex; i <= currentVersionIndex; i += 1) {
      cumulativeForceUpdateParams =
        cumulativeForceUpdateParams.concat(defaultAccountSettings[i].force_update_params);
    }
    const uniqueForceUpdateParams = [...new Set(cumulativeForceUpdateParams)];
    overwriteSiteSettingParams(mergedSiteSettingConfiguration,
      defaultAccountSettings[currentVersionIndex], uniqueForceUpdateParams);
  };

// Remove irrelevant metrics and widgets from newer configurations
const _adjustRollbackVersionsConfiguration =
  (defaultCurrentVersionSettings, mergedSiteSettingConfiguration) => {
    const unusedWidgets = mergedSiteSettingConfiguration.configuration.length -
      defaultCurrentVersionSettings.configuration.length;
    if (unusedWidgets > 0) {
      mergedSiteSettingConfiguration.configuration.splice(-unusedWidgets);
    }
    mergedSiteSettingConfiguration.configuration.forEach((widgetConfiguration, widgetIndex) => {
      const defaultSettingsMetricsCount =
        defaultCurrentVersionSettings.configuration[widgetIndex].metrics.length;
      const widgetMetricsCount = widgetConfiguration.metrics.length;
      if (widgetMetricsCount > defaultSettingsMetricsCount) {
        const unusedMetrics = widgetMetricsCount - defaultSettingsMetricsCount;
        widgetConfiguration.metrics.splice(-unusedMetrics);
      }
    });
  };

// Adjust widget configuration that doesn't require a change in site-setting
// For example, If you have permissions to Skills widget,
// it enables two new metrics in shift status widget
const _adjustVisibleOnlyConfiguration = ({ getters }, metricsConfiguration) => {
  each(WIDGET_LOGICAL_NAMES, (widget) => {
    const affectedWidgets = get(WIDGET_ENABLING_OTHER_WIDGETS_METRICS, widget);
    if (getters.getWidgetPermission(widget) && !isUndefined(affectedWidgets)) {
      each(Object.keys(affectedWidgets), (affectedWidget) => {
        const widgetIndex = metricsConfiguration.findIndex(
          widgetConfig => widgetConfig.widget_name === affectedWidget);
        if (widgetIndex > -1) {
          affectedWidgets[affectedWidget].forEach((metricName) => {
            const metricIndex =
              metricsConfiguration[widgetIndex].metrics.findIndex(
                metric => metric.name === metricName);
            if (metricIndex > -1) {
              const metricConfiguration =
                metricsConfiguration[widgetIndex].metrics[metricIndex];
              if (metricConfiguration) {
                set(metricConfiguration, 'config.enabled', true);
              }
            }
          });
        }
      });
    }
  });
};

const setMetricsConfiguration = async ({ getters, commit }, metricsConfiguration) => {
  _adjustVisibleOnlyConfiguration({ getters }, metricsConfiguration);
  commit('setMetricsConfiguration', metricsConfiguration);
  await updateUserSettingsInStore({ getters, commit });
};

const setMetricsConfigurationSiteSetting = async ({ getters, commit }, metricsConfiguration) => {
  const key = SESSION_SETTINGS.MANAGER_WORKSPACE_METRICS_CONFIGURATION;
  const value = {
    deleted: false,
    id: key,
    propertyValue: {
      value: cloneDeep(metricsConfiguration),
    },
    type: 4,
  };
  const revision = getters.getMetricsConfigurationRevision;
  const loggedInUserId = getters.getLoggedInUserId;
  let response;
  try {
    response = await acService.setSiteSetting(key, value, revision, loggedInUserId);
    const responseRevision = get(response, 'responseRevision');
    if (responseRevision) {
      commit('setMetricsConfigurationRevision', responseRevision);
    }
  } catch (error) {
    response = Promise.reject(error);
  }
  return response;
};

const initAccountSettings = async ({ getters, commit }) => {
  const siteSetting = getters.getSiteSetting({
    siteSettingKey: SESSION_SETTINGS.MANAGER_WORKSPACE_METRICS_CONFIGURATION,
  });
  const siteSettingConfiguration = get(siteSetting, 'configuration');
  parseMetricsConfiguration(siteSettingConfiguration, true);
  const siteSettingVersion = get(siteSetting, [SITE_SETTINGS_VERSION_KEY]);
  const siteSettingVersionNumber = convertVersionToNumber(siteSettingVersion);
  const currentAmwVersion = getters.getNumericAmwVersion;
  let mergedSiteSettingConfiguration = cloneDeep(siteSetting);
  // Assumptions: We always add new metrics and new widgets at the end of the existing array,
  // and we never delete from the configuration, only mark as deleted true
  // If version in Site-setting is equal to current AMW Version, merge shouldn't take place
  if (siteSettingVersionNumber !== currentAmwVersion) {
    // Version in Site-setting is not equal to current AMW Version
    const indexes = _getSettingsIndexes(siteSettingVersionNumber, currentAmwVersion,
      defaultAccountSettings);
    const currentVersionIndex = indexes.currentVersionIndex;
    const localVersionIndex = indexes.localVersionIndex;
    // We want to send mergeSiteSettingConfiguration also the numeric value of the current version
    // in order to be able to compare the version number with the MAX_AMW_VERSION_NUMBER
    // and that way to handle cases when the developer didn't send the 'developmentVersion'
    const currentAmwVersionData = { numeric: currentAmwVersion, value: getters.getAmwVersion };
    // We always start with merging the configuration between site-setting configuration and
    // the one we found to be the right one it the defaultSettings file.
    // This method also covers cases where the site-setting version is exactly one version
    // before the default site-setting version (standard upgrade) or it's not in the default
    // account settings mapping
    mergedSiteSettingConfiguration = mergeSiteSettingConfiguration(
      defaultAccountSettings[currentVersionIndex], siteSetting, currentAmwVersionData);
    if (!isNull(localVersionIndex) && currentVersionIndex > localVersionIndex) {
      // This is a case where we have skipped a few versions and not standard upgrade
      _adjustSkippedVersionsConfiguration(indexes, mergedSiteSettingConfiguration);
    } else if (localVersionIndex === currentVersionIndex) {
      // This is a case of rollback or version change within the same default settings configuration
      _adjustRollbackVersionsConfiguration(defaultAccountSettings[currentVersionIndex],
        mergedSiteSettingConfiguration);
    }
  }
  const updateNeeded =
    updateSiteSettingsPerSpecificFeature(
      mergedSiteSettingConfiguration,
      [WIDGET_LOGICAL_NAMES.CONVERSATIONS],
      [getMetricSettingsKey(WIDGET_LOGICAL_NAMES.CONVERSATIONS, METRIC_NAMES.BRAND_NAME)],
      getters.getHasFeature(FEATURES.CONTACT_CENTER),
      true)
    ||
    updateSiteSettingsPerSpecificFeature(
      mergedSiteSettingConfiguration,
      [WIDGET_LOGICAL_NAMES.CONVERSATIONS],
      [getMetricSettingsKey(WIDGET_LOGICAL_NAMES.CONVERSATIONS, METRIC_NAMES.CONVERSATION_INTENTS)],
      getters.hasLiveIntent,
      true)
  ;
  if (siteSettingVersionNumber !== currentAmwVersion || updateNeeded) {
    // Save site-settings in backend
    try {
      await setMetricsConfigurationSiteSetting({ getters, commit }, mergedSiteSettingConfiguration);
    } catch (e) {
      logger.error('error while updating the configuration in site settings on first load',
        'root::actions::initAccountSettings', JSON.stringify({ siteSettingVersion,
          amVersion: getters.getAmwVersion,
          loggedInUserId: getters.getLoggedInUserId,
          error_message: e.message,
          error_response_data: e.response.data,
        }));
    }
  }
  const metricsConfiguration = get(mergedSiteSettingConfiguration, 'configuration', siteSettingConfiguration);
  await setMetricsConfiguration({ getters, commit }, metricsConfiguration);
};

const _getLocalSettingsConfig = (getters) => {
  const localUserSettings = _getSettingsFromLocalstorage(getters);
  let localUserAmwVersion = MAX_AMW_VERSION_NUMBER;
  if (localUserSettings && has(localUserSettings, 'widget_settings_amw_version')) {
    localUserAmwVersion = convertVersionToNumber(localUserSettings.widget_settings_amw_version);
  }
  const currentAmwVersion = getters.getNumericAmwVersion;
  const indexes = _getSettingsIndexes(localUserAmwVersion, currentAmwVersion, defaultSettings);
  const defaultVersionSettings = defaultSettings[indexes.currentVersionIndex];
  return {
    localUserSettings,
    localUserAmwVersion,
    defaultVersionSettings,
  };
};

const initUserSettings = async ({ dispatch, getters, commit, rootState }) => {
  const localSettingsConfig = _getLocalSettingsConfig(getters);
  await _applyUserSettings(dispatch, getters, commit, localSettingsConfig);
  const localAlertSettings = _getAlertsFromLocalstorage(getters);
  commit('setAlertData', isNull(localAlertSettings) ? {} : localAlertSettings);
  // AC User settings will load all the AC settings related to MWS
  // as for now it loads Pagination settings
  await ACUserSettings
    .initialize({ userId: rootState.session.userId })
    .loadPaginationPageSize();
};

const saveLayoutInDb = ({ getters }) => {
  const userSettings = getters.getUserSettings;
  _setLocalStorageUserSettings(getters, JSON.stringify(userSettings));
};

const resetGridItemSize = ({ commit, getters }, options) => {
  commit('resetGridItemSize', options);
  saveLayoutInDb({ getters });
};

/* ****************************** ACCOUNT CONFIG ************************* */

/**
 * Validates that agent groups' filter complies with userManagedGroups after the last one changed
 */
const _resetFilteredGroupsByManaged = (commit, getters) => {
  const filterFunc = agentGroupId => includes(getters.getUserManagedGroupsIds, agentGroupId);
  const validGroupsForFilter = filter(getters.getGroupsIds, filterFunc);
  if (validGroupsForFilter.length !== getters.getGroupsIds.length) {
    commit('setAgentGroupsFilter', validGroupsForFilter);
  }
};

const _updateWidgetSubscriptions = (activeWidgets) => {
  eventBusEmitter.emit('updateWidgetsSubscription', activeWidgets);
};

const _updateGlobalFilterSubscriptions = ({ commit, getters }) => {
  if (commit) {
    // eslint-disable-next-line no-use-before-define
    setGlobalFiltersUpdated({ commit }, { value: true });
  }
  _updateWidgetSubscriptions(getters.getActiveWidgets);
};

const _resetFilteredSkills = (commit, getters) => {
  const filterFunc = skillId => includes(getters.getUserManagedSkillsIds, skillId);
  const skillsFilterIds = getters.getSkillsFilterIds;
  const validSkillsForFilter = filter(skillsFilterIds, filterFunc);
  if (validSkillsForFilter.length !== skillsFilterIds.length) {
    commit('setSkillsFilter', validSkillsForFilter);
  }
};

const _handleACUsersUpdate = (commit, getters, usersData) => {
  if (usersData && getters.getUsersRevision !== usersData.revision) {
    commit('setUsers', usersData);
    commit('setAgentGroupsUpdateRequired', true);
  }
};

const _handleACSkillsUpdate = (commit, getters, skillsData) => {
  if (skillsData && getters.getSkillsRevision !== skillsData.revision) {
    commit('setSkills', skillsData);
  }
};

const _handleACAgentGroupsUpdate = (dispatch, commit, getters, agentGroupsData, options) => {
  // this api always returns 200 in status. we update the store only if revision has changed
  // (ex: change in group) or if getAgentGroupsUpdateRequired is true (changes in users / members)
  const revision = agentGroupsData && agentGroupsData.revision;
  const currentRevision = getters.getAgentGroupsRevision;
  if (getters.getAgentGroupsUpdateRequired || (currentRevision !== revision)) {
    commit('setAgentGroups', agentGroupsData);
    commit('setAgentGroupsUpdateRequired', false);
    if (options.updateSubscription) {
      _updateGlobalFilterSubscriptions({ commit, getters });
    }
  }
};

const _handleACUserManagedDataUpdate = (dispatch, commit, getters, userManagedData, options) => {
  if (userManagedData && userManagedData.data && userManagedData.data.length > 0 &&
    userManagedData.data[0].skills && getters.getUserManagedRevision !== userManagedData.revision) {
    userManagedData.data[0].skills.push(UNASSIGNED_SKILL_ID);
    commit('setUserManagedData', userManagedData);
    if (options.updateSubscription) {
      _resetFilteredGroupsByManaged(commit, getters);
      _resetFilteredSkills(commit, getters);
      _updateGlobalFilterSubscriptions({ commit, getters });
    }
  }
};

const _handleACStatusReasonsUpdate = (commit, getters, statusReasons) => {
  if (statusReasons && getters.getStatusReasonsRevision !== statusReasons.revision) {
    commit('setStatusReasons', statusReasons);
  }
};

const _onACSubscriptionSuccess = (dispatch, commit, getters, response) => {
  logger.debug('', 'root::actions::_onACSubscriptionSuccess', response);
  switch (response && response.name) {
    case RESOURCES.SKILLS:
      _handleACSkillsUpdate(commit, getters, response.data);
      break;
    case RESOURCES.AGENT_GROUPS:
      _handleACAgentGroupsUpdate(dispatch, commit, getters, response.data,
        { updateSubscription: true });
      break;
    case RESOURCES.USER_MANAGED_DATA:
      _handleACUserManagedDataUpdate(dispatch, commit, getters, response.data,
        { updateSubscription: true });
      break;
    case RESOURCES.USERS:
      _handleACUsersUpdate(commit, getters, response.data);
      break;
    case RESOURCES.STATUS_REASON:
      _handleACStatusReasonsUpdate(commit, getters, response.data);
      break;
    default:
      logger.error('cannot resolve subscription name',
        'root::actions::_onACSubscriptionSuccess', response.name);
  }
};

const _handleACError = ({ getters }, resourceName) => {
  const subscriptionsStatus = getters.getAccountDataSubscriptionsStatus;
  if (subscriptionsStatus === SUBSCRIPTION_STATUSES.STARTED) {
    updateSubscriptionTimeout[resourceName] = setTimeout(() => {
      if (subscriptionsStatus === SUBSCRIPTION_STATUSES.STARTED) {
        _updateACSubscription(resourceName);
      }
    }, AC_RETRY_AFTER_ERROR_INTERVAL);
  }
};

const _onACSubscriptionError = ({ getters }, resourceName, err) => {
  logger.error('Account Config Subscription error. root::actions::_onACSubscriptionError', JSON.stringify({ resourceName, err }));
  const lastRequestTime = get(err, ['config', [AXIOS_RETRY_OBJECT], 'lastRequestTime']);
  const responseStatus = get(err, 'response.status');
  if (responseStatus === THROTTLING_STATUS_CODE) {
    // In case of throttling by AC, we want to log an error along with user id
    logger.error(`Account Config ${resourceName} Subscription error of type throttling(${THROTTLING_STATUS_CODE}) by user id: ${getters.getLoggedInUserId}`);
  }
  // This code handles an edge case where after we have returned to our tab after absence
  // of 30 seconds or more and there was still a pending request that failed after new subscriptions
  // started. In that case we don't want to restart subscription of the failed request
  // as new subscriptions were already initiated.
  if (!lastRequestTime || lastRequestTime >= lastSubscriptionTime[resourceName]) {
    _handleACError({ getters }, resourceName);
  }
};

const _handleACSubscriptions = async (dispatch, commit, getters, subscriptions) => {
  each(subscriptions, (subscriptionObj) => {
    subscriptionObj.subscription.clearAllEventListeners();
    subscriptionObj.subscription.on(Subscription.EVENTS.onSubscriptionUpdateNotification,
      _onACSubscriptionSuccess.bind(this, dispatch, commit, getters),
      'ACSubscriptionUpdateNotificationSuccess');
    subscriptionObj.subscription.on(Subscription.EVENTS.onSubscriptionError,
      _onACSubscriptionError.bind(this, { commit, getters }, subscriptionObj.name),
      'ACSubscriptionUpdateNotificationError');
    // const subscriptionPayload = _createACSubscriptionPayload();
    try {
      logger.debug('root::actions::before executing start subscription');
      subscriptionObj.subscription.start();
      lastSubscriptionTime[subscriptionObj.name] = moment();
    } catch (ex) {
      logger.error('cannot start AC subscription', 'start', { ex, subscriptionObj });
    }
  });
};

const _updateACSubscription = async (resourceName) => {
  const subscription =
    find(acSubscriptions, acSubscription => acSubscription.name === resourceName);
  if (subscription) {
    try {
      logger.debug('root::actions::before executing update subscription');
      subscription.subscription.update();
      lastSubscriptionTime[resourceName] = moment();
    } catch (ex) {
      logger.error('cannot update AC subscription', 'update', { ex, subscription });
    }
  }
};

const _clearSubscriptionTimeOut = () => {
  stopSubscriptionRequested = false;
  clearTimeout(stopSubscriptionTimeout);
};

const _clearUpdateSubscriptionTimeout = () => {
  each(Object.keys(updateSubscriptionTimeout), (resourceName) => {
    clearTimeout(updateSubscriptionTimeout[resourceName]);
  });
};

const _handleACUpdateData = (commit, getters, dispatch, fetchedACData) => {
  _handleACSkillsUpdate(commit, getters, fetchedACData && fetchedACData.skills);
  _handleACUsersUpdate(commit, getters, fetchedACData && fetchedACData.users);
  _handleACAgentGroupsUpdate(dispatch, commit, getters, fetchedACData && fetchedACData.agentGroups,
    { updateSubscription: false });
  _handleACUserManagedDataUpdate(dispatch, commit, getters,
    fetchedACData && fetchedACData.userManagedData,
    { updateSubscription: false });
  _handleACStatusReasonsUpdate(commit, getters, fetchedACData && fetchedACData.statusReasons);
};

const _startWidgetsSubscriptions = (activeWidgets) => {
  eventBusEmitter.emit('startWidgetsSubscription', activeWidgets);
};

const _setConsumerNamePermission = (commit, getters) => {
  const hasAgentProfile =
    getters.getHasPrivilege(PRIVILEGES.VIEW_MESSAGING_AVAILABILITY_INFO);
  const hasAgentManagerProfile = getters.getHasPrivilege(PRIVILEGES.SKILL_CHATS);
  const hasCampaignManagerProfile = getters.getHasPrivilege(PRIVILEGES.VIEW_CAMPAIGN_MODULE);
  const hasUsersManagerProfile = getters.getHasPrivilege(PRIVILEGES.VIEW_USERS_MODULE);
  const isSimpleAgent = hasAgentProfile &&
    !hasCampaignManagerProfile &&
    !hasUsersManagerProfile &&
    !hasAgentManagerProfile;
  const hasBlockConsumerName = getters.getSiteSetting({
    siteSettingKey: SESSION_SETTINGS.BLOCK_CONSUMER_NAME,
    isBoolean: true,
  });
  commit('setBlockConsumerName', hasBlockConsumerName && isSimpleAgent);
};

const _setConfigureManagerWorkspacePermission = (commit, getters) => {
  const hasConfigurationFeature =
    getters.getHasFeature(FEATURES.MANAGER_WORKSPACE_CONFIGURATION);
  const hasConfigurationPrivilege =
    getters.getHasPrivilege(PRIVILEGES.MANAGER_WORKSPACE_CONFIGURATION);
  commit('setCanConfigureManagerWorkspace', hasConfigurationFeature && hasConfigurationPrivilege);
};

const setInitialAccountConfigData = async ({ dispatch, commit, getters },
  forceFetchAcEntities = false) => {
  if (getters.getAccountDataStatus === DATA_LOADING_STATES.LOADING) {
    /*
    we do not want to initiate again the whole process (fetch all AC + initiate subscriptions)
    if the AC data loading state is in loading mode (the data is being fetched)
    */
    return;
  }
  if (stopSubscriptionRequested &&
    getters.getWidgetsSubscriptionsStatus === SUBSCRIPTION_STATUSES.STARTED) {
    // we initialize the app after we left (stopSubscriptionRequested is true)
    // less than 30 seconds ago.
    // let's clear the stop all subscriptions process timer
    _clearSubscriptionTimeOut();
  } else {
    if (!forceFetchAcEntities) {
      // this is the very first time we load the app (forceFetchAcEntities is by default false
      // because by default the ACService fetches only once the data
      // at the very beginning of the app initialization).
      // this is the only case we want to see the loading placeholder tiles
      commit('setAccountDataStatus', DATA_LOADING_STATES.LOADING);
    }
    let extraResourceConfig;
    const useWorkspacePartialWidgets = getters.useWorkspacePartialWidgets;
    if (useWorkspacePartialWidgets) {
      // if the logged in user is an agent we need to make some changes in the ac users api.
      // we need to retrieve the memberOf(agentGroupId) so we can find the group he's assigned to
      // as well as the skills list in order to display the list of the skills
      // relevant to all the agents.
      // belonging to this group to be displayed in the skills filter.
      // the extraResourceConfig variable holds all the extra parameters we want to pass to any
      // AC resources to handle such use cases.
      extraResourceConfig = {
        [RESOURCES.USERS]: {
          urlSearchParam: ',memberOf(agentGroupId),skillIds',
        },
      };
    }
    // let's get the AC subscriptions and eventually the AC data
    const ACPackage = await acService.getACPackage({
      loggedInUserId: getters.getLoggedInUserId,
      forceFetchAcEntities,
      extraResourceConfig,
      isLPA: getters.getIsLPA,
      contextName: SOURCE_CONTEXT_NAMES.ROOT,
    });
    const fetchedACData = cloneDeep(ACPackage.data);
    const acDataFailedToFetch = isNull(fetchedACData);
    const acDataNotFetched = isUndefined(fetchedACData);
    const acDataFetchedSuccessfully = !acDataFailedToFetch && !acDataNotFetched;
    acSubscriptions = ACPackage.subscriptions;
    if (!acDataFailedToFetch) {
      // if we have initial data (no matter if good or wrong format) or if we have the subscriptions
      // (data has been previously fetched), let's remove the loading placeholder tiles
      commit('setAccountDataStatus', DATA_LOADING_STATES.SUCCESS);
    }
    if (acDataFetchedSuccessfully) {
      _handleACUpdateData(commit, getters, dispatch, fetchedACData);
      _handlePresetValidation(commit, getters);
    }
    if (acDataFetchedSuccessfully ||
      (acDataNotFetched &&
        getters.getAccountDataSubscriptionsStatus !== SUBSCRIPTION_STATUSES.STARTED)) {
      // on initial AC fetch (no subscription started yet)
      // or if subscription is not in a start mode,
      // let's start the widgets and AC subscriptions
      _startWidgetsSubscriptions(getters.getActiveWidgets);
      commit('setWidgetsSubscriptionsStatus', SUBSCRIPTION_STATUSES.STARTED);
      _setConsumerNamePermission(commit, getters);

      _setConfigureManagerWorkspacePermission(commit, getters);
      await _handleACSubscriptions(dispatch, commit, getters, ACPackage.subscriptions);
      commit('setAccountDataSubscriptionStatus', SUBSCRIPTION_STATUSES.STARTED);
    }
    if (acDataFailedToFetch) {
      // ac api is down or exception occurred during the fetch? let's show an error sign
      // and retry in 10 seconds
      commit('setAccountDataStatus', DATA_LOADING_STATES.ERROR);
      setInitDataTimeout =
        setTimeout(() => {
          if (getters.getIsModuleActive) {
            setInitialAccountConfigData({ commit, getters, dispatch }, true);
          }
        }, AC_RETRY_AFTER_ERROR_INTERVAL);
    }
  }
};

const _handlePresetValidation = (commit, getters) => {
  const validatedOnce = getters.getPresetsValidatedOnFirstLoad;
  if (!validatedOnce) {
    commit('setPresetsValidatedOnFirstLoad');
    _validatePresetValues(commit, getters);
    _setLocalStorageUserSettings(getters, JSON.stringify(getters.getUserSettings));
  }
};

const stopACSubscriptions = async (commit, getters) => {
  const ACPackage = await acService.getACPackage({
    loggedInUserId: getters.getLoggedInUserId,
    isLPA: getters.getIsLPA,
    contextName: SOURCE_CONTEXT_NAMES.ROOT,
  });
  logger.debug('ACPackage', 'root::actions::stopACSubscriptions', ACPackage);
  each(ACPackage.subscriptions, (subscriptionObj) => {
    subscriptionObj.subscription.clearAllEventListeners();
    try {
      logger.debug('root::actions::before executing stop subscription');
      subscriptionObj.subscription.stop();
    } catch (ex) {
      logger.error('cannot stop AC subscription', 'stop', { ex, subscriptionObj });
    }
  });
  commit('setAccountDataSubscriptionStatus', SUBSCRIPTION_STATUSES.STOPPED);
};

const stopWidgetSubscriptions = (activeWidgets) => {
  eventBusEmitter.emit('stopWidgetsSubscription', activeWidgets);
};

const stopAllSubscriptions = async ({ commit, getters }) => {
  stopSubscriptionRequested = true;
  clearTimeout(setInitDataTimeout);
  if (getters.getAccountDataSubscriptionsStatus === SUBSCRIPTION_STATUSES.STARTED) {
    stopSubscriptionTimeout = setTimeout(async () => {
      if (getters.getAccountDataSubscriptionsStatus === SUBSCRIPTION_STATUSES.STARTED) {
        await stopACSubscriptions(commit, getters);
      }
      if (getters.getWidgetsSubscriptionsStatus === SUBSCRIPTION_STATUSES.STARTED) {
        stopWidgetSubscriptions(getters.getActiveWidgets);
        commit('setWidgetsSubscriptionsStatus', SUBSCRIPTION_STATUSES.STOPPED);
      }
      _clearSubscriptionTimeOut();
      _clearUpdateSubscriptionTimeout();
    }, STOP_SUBSCRIPTION_INTERVAL_SECONDS * 1000);
  }
};

const setTimeFrameFilter = ({ getters, commit }, timeFramefilter) => {
  commit('setTimeframe', timeFramefilter);
  _setUserFilter({ getters, commit }, 'timeframe', timeFramefilter);
  _updateGlobalFilterSubscriptions({ commit, getters });
};

const setAgentGroupFilter = ({ getters, commit }, agentGroups) => {
  commit('setAgentGroupsFilter', agentGroups);
  _setUserFilter({ getters, commit }, 'groups', agentGroups);
  _updateGlobalFilterSubscriptions({ commit, getters });
};

const setSkillsFilter = ({ getters, commit }, skills) => {
  commit('setSkillsFilter', skills);
  _setUserFilter({ getters, commit }, 'skills', skills);
  _updateGlobalFilterSubscriptions({ commit, getters });
};

const setAgentsFilter = ({ commit, dispatch, getters }, agents) => {
  dispatch(`${WIDGET_PATHS.AGENT_LIST}/${CHANGE_AGENTS_FILTER_ACTION_NAME}`, agents, { root: true });
  _updateGlobalFilterSubscriptions({ commit, getters });
};

const setGlobalFiltersUpdated = ({ commit }, data) => commit('setGlobalFiltersUpdating', data);

const changeNewManagerApiState = ({ commit }, useNewManagerApiManifestValue) => {
  commit('setNewManagerApiUsage', useNewManagerApiManifestValue !== false);
};

const changeFetchResponseMetricsState =
  async ({ commit }, fetchResponseMetricsManifestValue) => {
    commit('setFetchResponseMetricsState', fetchResponseMetricsManifestValue !== false);
  };

const changeEnableIntentsState =
  async ({ commit }, enableIntentsManifestValue) => {
    commit('setEnableIntentsState', enableIntentsManifestValue !== false);
  };

const changeEnableIntentsFilterState =
  async ({ commit }, enableIntentsFilterManifestValue) => {
    commit('setEnableIntentsFilterState', enableIntentsFilterManifestValue !== false);
  };

const setAmwVersion = ({ commit }, amwVersion) => {
  commit('setAmwVersion', amwVersion);
};

const fetchMetricsConfigurationSiteSetting = async ({ getters, commit }) => {
  await acService.getManagerWorkspaceSiteSettings().then((siteSettings) => {
    const revision = get(siteSettings, 'revision');
    const siteSettingsData = get(siteSettings, 'data');
    const metricsConfigurationSiteSetting = extractConfigurationFromSiteSetting(siteSettingsData);
    const metricsConfiguration = metricsConfigurationSiteSetting.metricsConfiguration;
    const metricsConfigurationVersion = metricsConfigurationSiteSetting.metricsConfigurationVersion;
    if (metricsConfiguration) {
      if (revision && revision !== getters.getMetricsConfigurationRevision) {
        commit('setMetricsConfigurationRevision', revision);
      }
      setMetricsConfiguration({ getters, commit }, metricsConfiguration);
      commit('setMetricsConfigurationVersion', metricsConfigurationVersion);
    }
  }).catch(err => logger.error(`Error fetching Manager Workspace site settings: ${err}`));
};

const setWorkspacePartialWidgets = ({ commit }) => {
  commit('setWorkspacePartialWidgets', true);
};

const setWidgetFullSize = ({ getters }, clientHeight) => {
  const widgets = getters.getVisibleWidgets;
  if (widgets.length === 1) {
    const settings = GRID_LAYOUT_GLOBAL_SETTINGS;
    const diffHeight = clientHeight - settings.MIN_RESOLUTION_CLIENT_HEIGHT;
    const diffWidgetRows = diffHeight / (settings.ROW_HEIGHT + settings.MARGIN_BOTTOM);
    const h = settings.MIN_RESOLUTION_TOTAL_GRID_ROWS + diffWidgetRows;
    const w = GRID_LAYOUT_GLOBAL_SETTINGS.NUMBER_OF_COLUMNS;
    eventBusEmitter.emit('resizeGridItem', { id: widgets[0].i, x: 0, y: 0, h, w });
  }
};

const setIsModuleActive = ({ commit }, isModuleActive) => {
  commit('setIsModuleActive', isModuleActive);
};

const setUseBotsGraphMock = ({ commit }, useBotsGraphMock) => {
  commit('setUseBotsGraphMock', useBotsGraphMock);
};

const setBotsGraphMock = ({ commit }, botsGraphMock) => {
  commit('setBotsGraphMock', botsGraphMock);
};

const setShowBotGraph = ({ commit }, showBotGraph) => {
  commit('setShowBotGraph', showBotGraph);
};

const updateBotGraphUserSettings = async ({ commit, getters }, botGraphUserSettings) => {
  const userSettings = cloneDeep(getters.getUserSettings);
  const botUserSettings = find(map(userSettings.widget_settings.settings, 'config'),
    widget => widget.i === WIDGET_LOGICAL_NAMES.BOTS);
  if (get(botGraphUserSettings, 'graphW')) {
    botUserSettings.graphW = botGraphUserSettings.graphW;
  }
  if (has(botGraphUserSettings, 'widgetMode')) {
    botUserSettings.widgetMode = botGraphUserSettings.widgetMode;
  }
  _updateUserSettingsStoreAndLocalStorage({ commit, getters }, userSettings);
};

const updateWidgetModeUserSettings = async ({ commit, getters }, options) => {
  const userSettings = cloneDeep(getters.getUserSettings);
  const widgetUserSettings = find(map(userSettings.widget_settings.settings, 'config'),
    widget => widget.i === options.widget);
  widgetUserSettings.widgetMode = options.widgetMode;
  _updateUserSettingsStoreAndLocalStorage({ commit, getters }, userSettings);
};

const changePreset = async ({ dispatch, getters, commit }, presetName) => {
  const selectedPreset = getters.getPresetByName(presetName);

  _validatePresetValues(commit, getters);
  _applyPresetToAllWidgets(dispatch, commit, selectedPreset);
  await _setLocalStorageUserSettings(getters, JSON.stringify(getters.getUserSettings));
  _updateGlobalFilterSubscriptions({ commit, getters });
};

const _validatePresetValues = (commit, getters) => {
  const allPresets = getters.getPresetsList;
  each(allPresets, (preset) => {
    if (preset.name !== PRESETS.DEFAULT_PRESET_NAME) {
      const isGlobalValid = _validateGlobalWidgetPreset(getters, preset);
      const agentsValidationRes = _validateAgentsWidgetPreset(getters, preset);
      const isConvValid = _validateConversationsWidgetPreset(getters, preset);
      const isBotsValid = _validateBotsWidgetPreset(getters, preset);
      const isValid = isGlobalValid
        && agentsValidationRes.isValid
        && isConvValid
        && isBotsValid;
      if (preset.isValid !== isValid) {
        preset.isValid = isValid;
        commit('updateExistingPreset', preset);
      }
      if (agentsValidationRes.updateLegacyMetricName) {
        commit('updateExistingPreset', preset);
      }
    }
  });
};

const _validateGlobalWidgetPreset = (getters, preset) => {
  const globalWidget = preset.widgets[WIDGET_LOGICAL_NAMES.GLOBAL];
  const currentSkillsFilter = globalWidget.skills || [];
  const currentGroupsFilter = globalWidget.groups || [];

  const skillsFilterFunc = skillId => includes(getters.getUserManagedSkillsIds, skillId);
  const validSkillsForFilter = filter(currentSkillsFilter, skillsFilterFunc);
  let isSkillsFilterValid = true;
  if (validSkillsForFilter.length !== currentSkillsFilter.length) {
    globalWidget.skills = validSkillsForFilter;
    isSkillsFilterValid = false;
    _appendValidationErrorTooltip(preset, PRESET_VALIDATION_I18N_KEYS.GLOBAL.SKILLS);
  }

  const groupsFilterFunc = agentGroupId => includes(getters.getUserManagedGroupsIds, agentGroupId);
  const validGroupsForFilter = filter(currentGroupsFilter, groupsFilterFunc);
  let isGroupsFilterValid = true;
  if (validGroupsForFilter.length !== currentGroupsFilter.length) {
    globalWidget.groups = validGroupsForFilter;
    isGroupsFilterValid = false;
    _appendValidationErrorTooltip(preset, PRESET_VALIDATION_I18N_KEYS.GLOBAL.GROUPS);
  }

  return isSkillsFilterValid && isGroupsFilterValid;
};

const _validateAgentsWidgetPreset = (getters, preset) => {
  const agentsWidget = preset.widgets[WIDGET_LOGICAL_NAMES.AGENTS];
  const currentAgentsFilter = agentsWidget.agentsFilter || [];
  let isValid = true;
  let updateLegacyMetricName = false;

  const validSAgentIdsForFilter = _getValidAgentIds(getters, currentAgentsFilter);
  if (currentAgentsFilter.length !== validSAgentIdsForFilter.length) {
    isValid = false;
    agentsWidget.agentsFilter = validSAgentIdsForFilter;
    _appendValidationErrorTooltip(preset, PRESET_VALIDATION_I18N_KEYS.AGENTS.AGENTS_FILTER);
  }

  // Replace deprecated metric name
  const path = `widgets.${WIDGET_LOGICAL_NAMES.AGENTS}.sort.columnName`;
  const legacyMetricName = get(preset, path);
  if (legacyMetricName === METRIC_NAMES.AGENT_LOAD_CONVERSATIONS) {
    set(preset, path, METRIC_NAMES.AGENT_VIEW_AGENT_LOAD);
    updateLegacyMetricName = true;
  }

  return { isValid, updateLegacyMetricName };
};

const _validateConversationsWidgetPreset = (getters, preset) => {
  const conversationWidget = preset.widgets[WIDGET_LOGICAL_NAMES.CONVERSATIONS];
  const currentAgentsFilter = conversationWidget.agentsFilter || [];
  let isAgentsFilterValid = true;

  const validSAgentIdsForFilter = _getValidAgentIds(getters, currentAgentsFilter);
  if (currentAgentsFilter.length !== validSAgentIdsForFilter.length) {
    isAgentsFilterValid = false;
    conversationWidget.agentsFilter = validSAgentIdsForFilter;
    _appendValidationErrorTooltip(preset, PRESET_VALIDATION_I18N_KEYS.CONVERSATIONS.AGENTS_FILTER);
  }
  return isAgentsFilterValid;
};

const _validateBotsWidgetPreset = (getters, preset) => {
  const botsWidget = preset.widgets[WIDGET_LOGICAL_NAMES.BOTS];
  const currentBotsFilter = botsWidget.botsFilter || [];
  let isBotsFilterValid = true;

  const validBotAgentIdsForFilter = _getValidAgentIds(getters, currentBotsFilter);
  if (currentBotsFilter.length !== validBotAgentIdsForFilter.length) {
    isBotsFilterValid = false;
    botsWidget.botsFilter = validBotAgentIdsForFilter;
    _appendValidationErrorTooltip(preset, PRESET_VALIDATION_I18N_KEYS.BOTS.BOTS_FILTER);
  }
  return isBotsFilterValid;
};

const _getValidAgentIds = (getters, selectedFilterIds) => {
  const validAgentIds = getters.getUserIdsIgnoreFilter;
  const filterFunc = agentId => includes(validAgentIds, agentId);
  return filter(selectedFilterIds, filterFunc);
};

const _appendValidationErrorTooltip = (preset, key) => {
  if (isEmpty(preset.tooltipText)) {
    preset.tooltipText = i18n.t('amd:preset_validation');
  }
  const translatedText = i18n.t(`amd:${key}`);
  preset.tooltipText += translatedText;
};

const savePreset = async ({ rootGetters, getters, commit }, { newPresetName, overwrite }) => {
  const allPresets = getters.getPresetsList;

  const isDefaultNameModification = lowerCaseCompare(newPresetName, i18n.t('amd:preset_default_name'))
    || lowerCaseCompare(newPresetName, PRESETS.DEFAULT_PRESET_NAME);
  if (isDefaultNameModification) {
    return { defaultNameModification: true };
  }

  const existingPreset = allPresets.find(item => item.name === newPresetName);
  if (existingPreset && !overwrite) {
    return { isDuplicate: true };
  }

  const isLimitExceeded = allPresets.length >= PRESETS.PRESETS_COUNT_LIMIT;
  if (!existingPreset && isLimitExceeded) {
    return { isLimitExceeded: true };
  }

  const newPresetValues = _gatherCurrentPresetValuesFromStore(rootGetters, getters, newPresetName);
  if (existingPreset) {
    commit('updateExistingPreset', newPresetValues);
  } else {
    commit('addNewPreset', newPresetValues);
  }

  await _setLocalStorageUserSettings(getters, JSON.stringify(getters.getUserSettings));
  return { isDuplicate: false };
};

const deletePreset = async ({ commit, dispatch, getters }, presetName) => {
  const currentPresetName = getters.getCurrentPresetName;

  commit('removePreset', presetName);

  if (currentPresetName === presetName) {
    // Local storage update is taken care of by changePreset method.
    dispatch('changePreset', PRESETS.DEFAULT_PRESET_NAME);
  } else {
    await _setLocalStorageUserSettings(getters, JSON.stringify(getters.getUserSettings));
  }
};

const _restorePresetValues = ({ dispatch, getters, commit }, newSettings) => {
  if (!isEmpty(newSettings) && getters.getCurrentPreset.name !== PRESETS.DEFAULT_PRESET_NAME) {
    _applyPresetToAllWidgets(dispatch, commit, getters.getCurrentPreset);
  }
};

const _applyPresetToAllWidgets = (dispatch, commit, preset) => {
  commit('setGlobalPresetValues', preset);

  const conversationPresetValues = _extractConversationPresetValuesFromPreset(preset);
  dispatch(`${WIDGET_PATHS.CONVERSATIONS}/applyPresetValues`, conversationPresetValues, { root: true });
  const agentListPresetValues = _extractAgentListPresetValuesFromPreset(preset);
  dispatch(`${WIDGET_PATHS.AGENT_LIST}/applyPresetValues`, agentListPresetValues, { root: true });
  const inQueuePresetValues = _extractInQueuePresetValuesFromPreset(preset);
  dispatch(`${WIDGET_PATHS.QUEUE_HEALTH}/applyPresetValues`, inQueuePresetValues, { root: true });
  const skillsWidgetPresetValues = _extractSkillsPresetValuesFromPreset(preset);
  dispatch(`${WIDGET_PATHS.SKILL_LIST}/applyPresetValues`, skillsWidgetPresetValues, { root: true });
  const botListPresetValues = _extractBotListPresetValuesFromPreset(preset);
  dispatch(`${WIDGET_PATHS.BOT_LIST}/applyPresetValues`, botListPresetValues, { root: true });
};

const _gatherCurrentPresetValuesFromStore = (rootGetters, getters, presetName) => {
  const globalFilter = {
    skills: getters.getSkillsFilterIds,
    groups: getters.getGroupsFilterIds,
    timeframe: getters.getTimeFrameFilter,
  };

  const widgets = {};
  widgets[WIDGET_LOGICAL_NAMES.GLOBAL] = globalFilter;
  widgets[WIDGET_LOGICAL_NAMES.CONVERSATIONS] = rootGetters[`${WIDGET_PATHS.CONVERSATIONS}/getPresetValues`] || {};
  widgets[WIDGET_LOGICAL_NAMES.AGENTS] = rootGetters[`${WIDGET_PATHS.AGENT_LIST}/getPresetValues`] || {};
  widgets[WIDGET_LOGICAL_NAMES.IN_QUEUE] = rootGetters[`${WIDGET_PATHS.QUEUE_HEALTH}/getPresetValues`] || {};
  widgets[WIDGET_LOGICAL_NAMES.SKILLS] = rootGetters[`${WIDGET_PATHS.SKILL_LIST}/getPresetValues`] || {};
  widgets[WIDGET_LOGICAL_NAMES.BOTS] = rootGetters[`${WIDGET_PATHS.BOT_LIST}/getPresetValues`] || {};

  return {
    name: presetName,
    isActionable: true,
    isSortable: true,
    isValid: true,
    widgets,
  };
};

const _extractConversationPresetValuesFromPreset = (preset) => {
  const agentsFilterList = get(preset, `widgets.${WIDGET_LOGICAL_NAMES.CONVERSATIONS}.agentsFilter`, []);
  const statusFilterList = get(preset, `widgets.${WIDGET_LOGICAL_NAMES.CONVERSATIONS}.statusFilter`, null);
  const liveIntentsFilter = get(preset, `widgets.${WIDGET_LOGICAL_NAMES.CONVERSATIONS}.liveIntentsFilter`, []);
  const sort = get(preset, `widgets.${WIDGET_LOGICAL_NAMES.CONVERSATIONS}.sort`, {
    columnName: METRIC_NAMES.CONVERSATIONS_RESPONSE_TIME,
    direction: SORT_DIRECTION.ASCENDING,
  });
  return {
    agentsFilterList,
    statusFilterList,
    liveIntentsFilter,
    sort,
  };
};

const _extractBotListPresetValuesFromPreset = (preset) => {
  const statusFilter = get(preset, `widgets.${WIDGET_LOGICAL_NAMES.BOTS}.statusFilter`, []);
  const botsFilter = get(preset, `widgets.${WIDGET_LOGICAL_NAMES.BOTS}.botsFilter`, []);
  const widgetMode = get(preset, `widgets.${WIDGET_LOGICAL_NAMES.BOTS}.widgetMode`, WIDGET_MODES.CARDS);
  const sort = get(preset, `widgets.${WIDGET_LOGICAL_NAMES.BOTS}.sort`, {
    columnName: METRIC_NAMES.AGENT_VIEW_ASSIGNED_CONVERSATIONS,
    direction: SORT_DIRECTION.DESCENDING,
  });
  return {
    statusFilter,
    botsFilter,
    widgetMode,
    sort,
  };
};

const _extractAgentListPresetValuesFromPreset = (preset) => {
  const statusFilter = get(preset, `widgets.${WIDGET_LOGICAL_NAMES.AGENTS}.statusFilter`, []);
  const agentsFilter = get(preset, `widgets.${WIDGET_LOGICAL_NAMES.AGENTS}.agentsFilter`, []);
  const widgetMode = get(preset, `widgets.${WIDGET_LOGICAL_NAMES.AGENTS}.widgetMode`, WIDGET_MODES.CARDS);
  const sort = get(preset, `widgets.${WIDGET_LOGICAL_NAMES.AGENTS}.sort`, {
    columnName: METRIC_NAMES.AGENT_VIEW_AGENT_LOAD,
    direction: SORT_DIRECTION.DESCENDING,
  });
  return {
    statusFilter,
    agentsFilter,
    widgetMode,
    sort,
  };
};

const _extractInQueuePresetValuesFromPreset = (preset) => {
  const sort = get(preset, `widgets.${WIDGET_LOGICAL_NAMES.IN_QUEUE}.sort`, {
    columnName: METRIC_NAMES.SKILL_UNASSIGNED_CONVERSATIONS,
    direction: SORT_DIRECTION.DESCENDING,
  });
  return {
    sort,
  };
};

const _extractSkillsPresetValuesFromPreset = (preset) => {
  const sort = get(preset, `widgets.${WIDGET_LOGICAL_NAMES.SKILLS}.sort`, {
    columnName: METRIC_NAMES.SKILL_ASSIGNED_CONVERSATIONS,
    direction: SORT_DIRECTION.DESCENDING,
  });
  return {
    sort,
  };
};

const setDialogAlertItems = ({ commit }, dialogAlertItems) => {
  commit('setDialogAlertItems', dialogAlertItems);
};

const resetDialogAlertItems = ({ commit }) => {
  commit('resetDialogAlertItems');
};

const getValidAlertData = item => !isUndefined(item.conditionType);

const setAlertData = async ({ commit, getters }, widgetAlertData) => {
  const alertData = cloneDeep(getters.getAlertData);
  const widgetAlertDataInStore = alertData[widgetAlertData.widgetName];
  const widgetAlertDataKeys = keys(widgetAlertData.data);
  const widgetAlertDataKeysInStore = keys(widgetAlertDataInStore);
  const widgetNotRelatedKeys = without(widgetAlertDataKeysInStore, widgetAlertDataKeys);
  let widgetNotRelatedData = pick(widgetAlertDataInStore, widgetNotRelatedKeys);
  widgetNotRelatedData = { ...widgetNotRelatedData, ...widgetAlertData.data };
  widgetNotRelatedData = pickBy(widgetNotRelatedData, getValidAlertData);
  alertData[widgetAlertData.widgetName] = widgetNotRelatedData;
  commit('setAlertData', alertData);
  await _setLocalStorageUserAlerts(getters, JSON.stringify(alertData));
};

export default {
  initUserSettings,
  initAccountSettings,
  setTimeFrameFilter,
  setAgentGroupFilter,
  setSkillsFilter,
  setAgentsFilter,
  setInitialAccountConfigData,
  stopAllSubscriptions,
  setGlobalFiltersUpdated,
  changeNewManagerApiState,
  changeFetchResponseMetricsState,
  changeEnableIntentsState,
  changeEnableIntentsFilterState,
  setAmwVersion,
  setMetricsConfiguration,
  fetchMetricsConfigurationSiteSetting,
  setMetricsConfigurationSiteSetting,
  updateUserSettingsInStore,
  setWorkspacePartialWidgets,
  setWidgetFullSize,
  setIsModuleActive,
  setUseBotsGraphMock,
  setBotsGraphMock,
  updateBotGraphUserSettings,
  changePreset,
  savePreset,
  deletePreset,
  setShowBotGraph,
  setAlertData,
  setDialogAlertItems,
  resetDialogAlertItems,
  saveDisplayState,
  saveLayoutInDb,
  resetGridItemSize,
  updateWidgetModeUserSettings,
};
